import React, { useEffect, useState } from "react";
import api from "../../api";
import styles from "./DailyTodoList.module.css";

const DIFFICULTY_OPTIONS = ["상", "중", "하"];
const STATUS_OPTIONS = ["미진행", "진행중", "완료"];

const emptyItem = (nextPriority) => ({
  priority: nextPriority,
  task_name: "",
  task_detail: "",
  difficulty: "중",
  estimated_minutes: "",
  status: "미진행",
});

// textarea 높이를 내용에 맞게 자동 조절
const autoResize = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

function getSelfId() {


try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u?.id ?? null;
  } catch {
    return null;
  }
}

export default function DailyTodoList() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [todoList, setTodoList] = useState(null);
  const [items, setItems] = useState([]);
  const [comment, setComment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState(emptyItem(1));

  // ✅ 팀장 전용 직원 선택
  const [employees, setEmployees] = useState(null); // null이면 권한 없음(드롭다운 숨김)
  const [selectedUserId, setSelectedUserId] = useState(""); // ""면 내 업무

  // ✅ 행 단위 수정 모드
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  // ✅ 팀장 코멘트 작성
  const [commentDraft, setCommentDraft] = useState("");

  // ✅ 코멘트 인라인 수정
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [editingCommentText, setEditingCommentText] = useState("");

  useEffect(() => {
    api
      .get("/daily-todo-lists/employees/")
      .then((res) => setEmployees(res.data))
      .catch(() => setEmployees(null)); // 팀장 권한이 아니면 403 -> 드롭다운 숨김
  }, []);

  const fetchToday = async (targetDate, userId) => {
    setLoading(true);
    try {
      const res = await api.get("/daily-todo-lists/today/", {
        params: { date: targetDate, ...(userId ? { user_id: userId } : {}) },
      });
      setTodoList(res.data);
      const loadedItems = res.data.items || [];
      setItems(loadedItems);
      setComment(res.data.leader_comment);
      setCommentDraft("");
      setNewItem(emptyItem(loadedItems.length + 1));
      setEditingId(null);
      setIsEditingComment(false);
      setEditingCommentText("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToday(date, selectedUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedUserId]);

  const handleAddItem = async () => {
    if (!newItem.task_name.trim()) return;
    if (items.length >= 6) {
      alert("주요 업무는 최대 6개까지 권장됩니다. 그래도 추가하시겠어요?");
    }
    const res = await api.post("/daily-todo-items/", {
      todo_list: todoList.id,
      ...newItem,
      priority: Number(newItem.priority) || items.length + 1,
      estimated_minutes: newItem.estimated_minutes || null,
    });
    const updated = [...items, res.data].sort((a, b) => a.priority - b.priority);
    setItems(updated);
    setNewItem(emptyItem(updated.length + 1));
  };

  // 상태(완료여부)는 자주 바뀌므로 수정모드 없이 즉시 반영
  const handleStatusChange = async (id, statusValue) => {
    const res = await api.patch(`/daily-todo-items/${id}/`, { status: statusValue });
    setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      priority: item.priority,
      task_name: item.task_name,
      task_detail: item.task_detail,
      difficulty: item.difficulty,
      estimated_minutes: item.estimated_minutes || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const handleSaveEdit = async (id) => {
    const res = await api.patch(`/daily-todo-items/${id}/`, {
      ...editDraft,
      priority: Number(editDraft.priority) || 1,
      estimated_minutes: editDraft.estimated_minutes || null,
    });
    const updated = items
      .map((it) => (it.id === id ? res.data : it))
      .sort((a, b) => a.priority - b.priority);
    setItems(updated);
    setEditingId(null);
    setEditDraft(null);
  };

  const handleDeleteItem = async (id) => {
    const target = items.find((it) => it.id === id);
    const msg = target?.is_carried_over
      ? "이월된 업무입니다. 삭제하면 과거 날짜의 동일 업무 기록도 함께 삭제됩니다. 삭제하시겠어요?"
      : "이 업무를 삭제하시겠어요? (이후 이월된 기록이 있다면 함께 삭제됩니다)";
    if (!window.confirm(msg)) return;
    await api.delete(`/daily-todo-items/${id}/`);
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleExcelDownload = async () => {
    const res = await api.get("/daily-todo-lists/export-excel/", {
      params: { date },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `daily_todo_${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // ✅ 팀장 코멘트 등록/수정 (팀장 권한일 때만 입력창 노출)
  const handleSaveComment = async () => {
    const targetUserId = selectedUserId || getSelfId();
    if (!targetUserId) {
      alert("대상 직원을 확인할 수 없습니다.");
      return;
    }
    if (!commentDraft.trim()) return; // 빈 내용 등록 방지
    await api.post("/leader-comments/", {
      target_user: targetUserId,
      date,
      content: commentDraft,
    });
    // 작성자명 등 최신 정보 반영을 위해 다시 조회
    fetchToday(date, selectedUserId);
  };

  // ✅ 코멘트 수정: 저장된 박스가 그 자리에서 입력창으로 바뀜
  const handleEditComment = () => {
    setIsEditingComment(true);
    setEditingCommentText(comment?.content || "");
  };

  const handleEditCommentCancel = () => {
    setIsEditingComment(false);
    setEditingCommentText("");
  };

  const handleEditCommentSave = async () => {
    const content = editingCommentText.trim();
    if (!content) return;
    const targetUserId = selectedUserId || getSelfId();
    await api.post("/leader-comments/", {
      target_user: targetUserId,
      date,
      content,
    });
    setIsEditingComment(false);
    setEditingCommentText("");
    fetchToday(date, selectedUserId);
  };

  // ✅ 코멘트 삭제
  const handleDeleteComment = async () => {
    if (!comment?.id) return;
    if (!window.confirm("팀장 코멘트를 삭제하시겠어요?")) return;
    await api.delete(`/leader-comments/${comment.id}/`);
    fetchToday(date, selectedUserId);
  };


  if (loading) return <div className={styles.loading}>불러오는 중...</div>;

  const pageTitle = todoList?.user_name
    ? `${todoList.user_name}님의 오늘의 주요 업무 (일별 TO DO)`
    : "오늘의 주요 업무 (일별 TO DO)";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{pageTitle}</h2>
        <div className={styles.headerActions}>
          {employees && (
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className={styles.employeeSelect}
            >
              <option value="">내 업무</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          )}
          <input type="date" className={styles.dateInput} value={date} onChange={(e) => setDate(e.target.value)} />



          <button onClick={handleExcelDownload} className={styles.excelBtn}>
            엑셀 다운로드
          </button>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>우선순위</th>
            <th>업무명</th>
            <th>업무내용</th>
            <th>난이도</th>
            <th>예상(분)</th>


            <th>완료여부</th>
            <th>이월</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isEditing = editingId === item.id;
            return (
              <tr key={item.id} className={item.is_carried_over ? styles.carriedRow : ""}>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      min="1"
                      className={styles.editInput}
                      value={editDraft.priority}
                      onChange={(e) => setEditDraft({ ...editDraft, priority: e.target.value })}
                    />
                  ) : (
                    item.priority
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      className={styles.editInput}
                      value={editDraft.task_name}
                      onChange={(e) => setEditDraft({ ...editDraft, task_name: e.target.value })}
                    />
                  ) : (
                    item.task_name
                  )}
                </td>

                <td className={styles.detailCell}>
                  {isEditing ? (


                  <textarea
                    className={styles.editTextarea}
                    rows={1}
                    ref={autoResize}
                    value={editDraft.task_detail}
                    onChange={(e) => {
                      autoResize(e.target);
                      setEditDraft({ ...editDraft, task_detail: e.target.value });
                    }}
                  />



                  ) : (
                    item.task_detail
                  )}
                </td>



                <td>
                  {isEditing ? (
                    <select
                      className={styles.editInput}
                      value={editDraft.difficulty}
                      onChange={(e) => setEditDraft({ ...editDraft, difficulty: e.target.value })}
                    >
                      {DIFFICULTY_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  ) : (
                    item.difficulty
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      className={styles.editInput}
                      value={editDraft.estimated_minutes}
                      onChange={(e) =>
                        setEditDraft({ ...editDraft, estimated_minutes: e.target.value })
                      }
                    />
                  ) : (
                    item.estimated_minutes || "-"
                  )}
                </td>
                <td>
                  <select
                    value={item.status}
                    className={styles[`status_${item.status}`]}
                    onChange={(e) => handleStatusChange(item.id, e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{item.is_carried_over && <span className={styles.badge}>이월</span>}</td>
                <td className={styles.actionCell}>
                  {isEditing ? (
                    <>
                      <button onClick={() => handleSaveEdit(item.id)} className={styles.saveBtn}>
                        저장
                      </button>
                      <button onClick={handleCancelEdit} className={styles.cancelBtn}>
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleStartEdit(item)} className={styles.editBtn}>
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className={styles.deleteBtn}
                      >
                        삭제
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}

          {items.length < 6 && (
            <tr className={styles.addRow}>
              <td>
                <input
                  type="number"
                  min="1"
                  value={newItem.priority}
                  onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
                />
              </td>
              <td>
                <input
                  placeholder="업무명"
                  value={newItem.task_name}
                  onChange={(e) => setNewItem({ ...newItem, task_name: e.target.value })}
                />
              </td>

              <td>
                <textarea
                  className={styles.addTextarea}
                  placeholder="업무내용"
                  rows={1}
                  ref={autoResize}
                  value={newItem.task_detail}
                  onChange={(e) => {
                    autoResize(e.target);
                    setNewItem({ ...newItem, task_detail: e.target.value });
                  }}
                />
              </td>


              <td>
                <select
                  value={newItem.difficulty}
                  onChange={(e) => setNewItem({ ...newItem, difficulty: e.target.value })}
                >
                  {DIFFICULTY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="number"
                  placeholder="예상(분)"
                  value={newItem.estimated_minutes}
                  onChange={(e) => setNewItem({ ...newItem, estimated_minutes: e.target.value })}
                />
              </td>
              <td className={styles.mutedCell}>미진행</td>
              <td></td>
              <td>
                <button onClick={handleAddItem} className={styles.addBtn}>
                  + 추가
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className={styles.commentBox}>
        <h4>팀장 코멘트</h4>

        {comment?.content && (
          <div className={styles.existingComment}>
            {isEditingComment ? (
              <>
                <textarea
                  className={styles.inlineEditTextarea}
                  value={editingCommentText}
                  autoFocus
                  onChange={(e) => setEditingCommentText(e.target.value)}
                />
                <div className={styles.commentBtns}>
                  <button onClick={handleEditCommentSave} className={styles.commentEditBtn}>
                    저장
                  </button>
                  <button onClick={handleEditCommentCancel} className={styles.commentDeleteBtn}>
                    취소
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.existingCommentText}>
                  {comment.content}
                  {comment.author_name && (
                    <div className={styles.commentMeta}>— {comment.author_name}</div>
                  )}
                </div>
                {employees && (
                  <div className={styles.commentBtns}>
                    <button onClick={handleEditComment} className={styles.commentEditBtn}>
                      수정
                    </button>
                    <button onClick={handleDeleteComment} className={styles.commentDeleteBtn}>
                      삭제
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}





        {employees ? (
          // ✅ 팀장 권한이면 코멘트 작성/수정 가능
          <div className={styles.commentEditRow}>
            <textarea
              placeholder="이 직원에게 남길 코멘트를 입력하세요"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
            />
            <button onClick={handleSaveComment} className={styles.commentSaveBtn}>
              등록
            </button>
          </div>
        ) : (
          !comment?.content && <p>아직 코멘트가 없습니다.</p>
        )}
      </div>
    </div>
  );
}